/* eslint-disable react/require-default-props */
import contains from 'document.contains';
import { kebabCase, mapKeys } from 'lodash';
import { Component } from 'react';

export type Display = 'block' | 'flex' | 'inline' | 'inline-block' | 'contents';

interface Props {
  className?: string;
  disabled?: boolean;
  width?: number;
  display?: Display;
  allowed?: (target: HTMLElement) => boolean;
  useCapture?: boolean;
  onOutsideClick?: (event: MouseEvent) => void;
  data?: Record<string, string>;
  dataCy?: string;
  children: React.ReactNode;
}

export class OutsideClickHandler extends Component<
  Props,
  Record<string, unknown>
> {
  private removeMouseUp: (() => void) | null = null;

  private removeMouseDown: (() => void) | null = null;

  private childNode: HTMLElement | null = null;

  constructor(props: Props) {
    super(props);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.setChildNodeRef = this.setChildNodeRef.bind(this);
    this.isAllowed = this.isAllowed.bind(this);
  }

  componentDidMount() {
    const { disabled, useCapture } = this.props;

    if (!disabled) {
      this.addMouseDownEventListener(useCapture);
    }
  }

  componentDidUpdate({ disabled: prevDisabled }: Props) {
    const { disabled, useCapture } = this.props;

    if (prevDisabled !== disabled) {
      if (disabled) {
        this.removeEventListeners();
      } else {
        this.addMouseDownEventListener(useCapture);
      }
    }
  }

  componentWillUnmount() {
    this.removeEventListeners();
  }

  /**
   * Use mousedown/mouseup to enforce that clicks remain outside the root's
   * descendant tree, even when dragged. This should also get triggered on
   * touch devices.
   */
  onMouseDown(e: MouseEvent) {
    const { useCapture } = this.props;
    const allowed = this.isAllowed(e.target as HTMLElement);

    if (!allowed) {
      if (this.removeMouseUp != null) {
        this.removeMouseUp?.();
        this.removeMouseUp = null;
      }
      document.addEventListener('mouseup', this.onMouseUp, {
        capture: useCapture,
      });
      this.removeMouseUp = () => {
        document.removeEventListener('mouseup', this.onMouseUp);
      };
    }
  }

  /**
   * Use mousedown/mouseup to enforce that clicks remain outside the root's
   * descendant tree, even when dragged. This should also get triggered on
   * touch devices.
   * */
  onMouseUp(e: MouseEvent) {
    const { onOutsideClick } = this.props;
    const allowed = this.isAllowed(e.target as HTMLElement);

    if (this.removeMouseUp != null) {
      this.removeMouseUp();
      this.removeMouseUp = null;
    }
    if (!allowed) {
      onOutsideClick?.(e);
    }
  }

  setChildNodeRef(ref: HTMLDivElement | null) {
    this.childNode = ref;
  }

  private isAllowed(target: HTMLElement): boolean {
    const { allowed } = this.props;
    const isAllowed = allowed?.(target) || false;
    const isChild = this.childNode != null && contains(this.childNode, target);

    return isAllowed || isChild;
  }

  addMouseDownEventListener(useCapture?: boolean) {
    document.addEventListener('mousedown', this.onMouseDown, {
      capture: useCapture,
    });
    this.removeMouseDown = () =>
      document.removeEventListener('mousedown', this.onMouseDown);
  }

  removeEventListeners() {
    this.removeMouseDown?.();
    this.removeMouseUp?.();
  }

  render() {
    const {
      children,
      display,
      className,
      width,
      data = {},
      dataCy,
    } = this.props;
    const custom_attributes = mapKeys(
      data,
      (_, key) => `data-${kebabCase(key)}`,
    );

    return (
      <div
        data-cy={dataCy}
        className={className}
        {...custom_attributes}
        ref={this.setChildNodeRef}
        style={{
          ...(display != null ? { display } : undefined),
          ...(width != null ? { width: `${width}px` } : undefined),
        }}
      >
        {children}
      </div>
    );
  }
}
